import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation, useParams } from '@tanstack/react-router';
import { ListPreference } from '@piwikpro/user-preferences';
import { RouterService, buildPaginatedPath } from '@piwikpro/router-crate';
import { DebouncedFunc, debounce } from 'lodash';
import { TFunction } from 'react-i18next';
import {
  Stack,
  StyledContainer,
  Pagination,
  Form,
  TextField,
  Button,
  List,
  Information,
  withTranslation,
} from '@piwikpro/ui-components';
import {
  Pagination as PaginationManager,
  errorBoundary,
  withInject,
  ConfigService,
  InternalListLink,
} from '@piwikpro/platform';
import { UserGroupAttributes, UserGroupId, UserGroups } from '@piwikpro/user-groups-crate';
import { IMembersState } from '@piwikpro/user-groups-crate/redux/details/members';
import { OrganizationAuthSettingsState } from '@piwikpro/auth-crate/reducers/auth';

interface UserGroupMembersProps {
  userGroups: UserGroups
  userGroupId: UserGroupId
  userGroupExternalId: UserGroupAttributes['external_id']
  isSamlEnabled: OrganizationAuthSettingsState['saml']['enabled']
  isAutoProvisioningEnabled: OrganizationAuthSettingsState['autoProvisioning']['enabled']
  userPreferences: any
  routerService: RouterService
  config: ConfigService
  Title: typeof React.Component
  total: number
  failed: IMembersState['failed']
  isLoading: IMembersState['isLoading']
  t: TFunction
  members: any
}

const UserGroupMembers: React.FC<UserGroupMembersProps> = ({
  userGroupId,
  userGroupExternalId,
  isSamlEnabled,
  isAutoProvisioningEnabled,
  routerService,
  userPreferences,
  config,
  isLoading,
  failed,
  total,
  t,
  Title,
  members,
  userGroups,
}) => {
  const params = useParams({
    from: '/_secured/administration/user-groups/$groupId/members/$page/$limit',
  });
  const { pathname } = useLocation();
  const [search, setSearch] = useState('');
  const [searchDebounce, setSearchDebounce] = useState<DebouncedFunc<any> | null>(null);
  const listPreference = new ListPreference(userPreferences.load(), '');
  const shouldShowExternalIdpInfo = Boolean(
    isSamlEnabled && isAutoProvisioningEnabled && userGroupExternalId,
  );

  const fetchUserGroupMembersData = async () => {
    const pagination = PaginationManager.fromQueryParams([], 0, params);
    try {
      await userGroups.fetchUserGroupMembers(userGroupId, {
        search,
        limit: pagination.getLimit(),
        offset: pagination.offset,
      });
      // eslint-disable-next-line no-empty
    } catch { }
  };
  useEffect(() => {
    fetchUserGroupMembersData();
  }, [userGroups.fetchUserGroupMembers, userGroupId, search]);

  const handleLimitChange = async (limit) => {
    try {
      await userGroups.fetchUserGroupMembers(userGroupId, {
        search,
        limit,
        offset: 0,
      });
    } catch {
      // eslint-disable-next-line no-empty
    }

    listPreference.limit = limit;
    userPreferences.persist(listPreference);
    routerService.push(buildPaginatedPath(pathname, 1, limit));
  };

  const handlePageChange = async (offset, pagination) => {
    try {
      await userGroups.fetchUserGroupMembers(userGroupId, {
        search,
        limit: pagination.getLimit(),
        offset,
      });
      // eslint-disable-next-line no-empty
    } catch { }

    routerService.push(buildPaginatedPath(
      pathname,
      pagination.getPageFromOffset(offset),
      pagination.getLimit(),
    ));
  };

  const handleSearchChange = (event) => {
    const pagination = PaginationManager.fromQueryParams([], 0, params);

    if (searchDebounce) {
      searchDebounce.cancel();
    }

    const handleSearch = debounce(async () => {
      try {
        await userGroups.fetchUserGroupMembers(userGroupId, {
          search,
          offset: 0,
          limit: pagination.getLimit(),
        });
        // eslint-disable-next-line no-empty
      } catch { }

      routerService.push(buildPaginatedPath(pathname, 1, pagination.getLimit()));
    }, config.get('LIST_SEARCH_DELAY'));

    setSearchDebounce(() => handleSearch);
    setSearch(event.target.value);

    handleSearch();
  };

  const handleDeleteUser = async (values, { setSubmitting }) => {
    const pagination = PaginationManager.fromQueryParams([], 0, params);

    try {
      await userGroups.deleteMemberFromUserGroup(values.groupId, values.memberId);

      if (members.length === 1 && pagination.getPage() > 1) {
        await userGroups.fetchUserGroupMembers(userGroupId, {
          search,
          limit: pagination.getLimit(),
          offset: pagination.offset - pagination.getLimit(),
        });
        routerService.push(buildPaginatedPath(
          pathname,
          pagination.getPage() - 1,
          pagination.getLimit(),
        ));
      } else {
        await userGroups.fetchUserGroupMembers(userGroupId, {
          search,
          limit: pagination.getLimit(),
          offset: pagination.offset,
        });
      }
    } catch (err) {
      // Handle error
    }

    setSubmitting(false);
  };

  const renderMemberRow = useCallback(member => (
    <List.Item key={member.id}>
      <InternalListLink
        to="/administration/users/$userId"
        params={{ userId: member.id }}
        label={member.attributes.email}
      />
      <Form
        id="user-group-member-from"
        initialValues={{
          groupId: userGroupId,
          memberId: member.id,
        }}
        validationSchema={() => { }}
        onSubmit={handleDeleteUser}
        render={({ isSubmitting, handleSubmit }) => (
          <Button
            icon="cross"
            appearance="subdued"
            disabled={isSubmitting}
            onClick={handleSubmit}
          />
        )}
      />
    </List.Item>
  ), [config, handleDeleteUser, userGroupId]);

  const pagination = PaginationManager.fromQueryParams(members, total, params);

  return (
    <Title part={t('user-groups.details.members.title')}>
      <Stack vertical horizontalAlignment="fill" spacing="wide">
        {shouldShowExternalIdpInfo && (
          <Stack.Item fill>
            <Information type="info">{t('user-groups.details.members.external-id.info')}</Information>
          </Stack.Item>
        )}
        <Stack.Item previousSpacing="default" fill>
          {!failed && (
            <StyledContainer
              border={['left', 'right', 'top']}
              padding="narrow"
              backgroundColor="gray"
            >
              <TextField
                name="search"
                icon="search"
                placeholder={t('user-groups.details.members.searchPlaceholder')}
                value={search}
                onChange={handleSearchChange}
                clearable
              />
            </StyledContainer>
          )}
          <List
            columns={[
              {
                size: 7,
                align: 'left',
              },
              {
                size: 1,
                align: 'right',
              },
            ]}
            error={failed}
            isLoading={isLoading}
          >
            {!failed && members.map(member => renderMemberRow(member))}
          </List>
        </Stack.Item>
        {(!failed && members.length > 0) && (
          <Stack.Item fill>
            <Pagination
              onLimitChange={handleLimitChange}
              limit={pagination.getLimit()}
              total={total}
              limitOptions={config.get('PAGINATION_ITEMS_PER_PAGE_OPTIONS')}
              startIndex={pagination.offset}
              endIndex={pagination.offset + pagination.getLimit()}
              onPageChange={e => handlePageChange(e, pagination)}
            />
          </Stack.Item>
        )}
      </Stack>
      <Outlet />
    </Title>
  );
};

export default withInject({
  config: 'config',
  userPreferences: 'userPreferences',
  Title: 'TitleCrate.components.Title',
  userGroups: 'UserGroupsCrate.userGroups',
  routerService: 'RouterCrate.router',
})(withTranslation(['administration'])(errorBoundary(UserGroupMembers)));
